import { journeyGetJson, journeyPostJson } from 'src/utils/journeyApi';
import { EVENT_TO_CHECKLIST_ITEM_MAPPING } from './constants';

export interface OnboardingChecklistItem {
  id: number;
  item_type: string;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingChecklistProgress {
  progress: OnboardingChecklistItem[];
  all_completed: boolean;
  total_items: number;
  completed_items: number;
  should_show_checklist: boolean;
}

export interface ToggleResponse {
  item: OnboardingChecklistItem;
  all_completed: boolean;
  total_items: number;
  completed_items: number;
}

export class OnboardingChecklistService {
  private static getApiUrl(organizationId: string, endpoint: string = '') {
    return `/api/organizations/${organizationId}/onboarding-checklist${endpoint}`;
  }

  /**
   * Get the current onboarding checklist progress for an organization
   */
  static async getProgress(organizationId: string): Promise<OnboardingChecklistProgress> {
    try {
      const apiUrl = this.getApiUrl(organizationId);
      
      const response = await journeyGetJson(apiUrl);
      
      return response;
    } catch (error) {
      console.error('OnboardingChecklistService: Failed to fetch onboarding checklist progress:', error);
      throw error;
    }
  }

  /**
   * Toggle the completion status of an onboarding checklist item
   */
  static async toggleItem(
    organizationId: string, 
    itemType: string, 
    isCompleting: boolean
  ): Promise<ToggleResponse> {
    try {
      const response = await journeyPostJson(
        this.getApiUrl(organizationId, `/${itemType}/toggle`),
        { is_completing: isCompleting }
      );
      return response;
    } catch (error) {
      console.error('Failed to toggle onboarding checklist item:', error);
      throw error;
    }
  }

  /**
   * Reset all onboarding checklist items to incomplete
   */
  static async resetChecklist(organizationId: string): Promise<{ message: string; progress: OnboardingChecklistItem[] }> {
    try {
      const response = await journeyPostJson(this.getApiUrl(organizationId, '/reset'));
      return response;
    } catch (error) {
      console.error('Failed to reset onboarding checklist:', error);
      throw error;
    }
  }

  /**
   * Mark an item as completed based on an event (for automatic completion)
   */
  static async markItemCompletedFromEvent(
    organizationId: string, 
    eventType: string
  ): Promise<boolean> {
    try {
      // Use centralized event mapping
      const eventMapping = EVENT_TO_CHECKLIST_ITEM_MAPPING;

      const itemType = eventMapping[eventType];
      if (!itemType) {
        console.warn(`Unknown event type: ${eventType}`);
        return false;
      }

      // Mark the item as completed
      await this.toggleItem(organizationId, itemType, true);
      return true;
    } catch (error) {
      console.error('Failed to mark item completed from event:', error);
      return false;
    }
  }

  /**
   * Get or update onboarding checklist settings
   */
  static async getChecklistSettings(organizationId: string): Promise<{ should_show_checklist: boolean }> {
    try {
      const response = await journeyGetJson(this.getApiUrl(organizationId, '/settings'));
      return response;
    } catch (error) {
      console.error('Failed to get checklist settings:', error);
      throw error;
    }
  }

  static async updateChecklistSettings(
    organizationId: string, 
    shouldShowChecklist: boolean
  ): Promise<{ message: string; settings: any }> {
    try {
      const response = await journeyPostJson(
        this.getApiUrl(organizationId, '/settings'),
        { should_show_checklist: shouldShowChecklist }
      );
      return response;
    } catch (error) {
      console.error('Failed to update checklist settings:', error);
      throw error;
    }
  }
}
